(() => {
  if (window.__qlsMobileCartDrawerSummaryInstalled) return;
  window.__qlsMobileCartDrawerSummaryInstalled = true;

  const mobileQuery = window.matchMedia('(max-width: 767px)');
  const gestureThreshold = 9;
  let initializedPanel = null;
  let outsideGesture = null;
  let actionDialog = null;
  let actionDialogOpener = null;
  let shareCartUrl = '';
  let pageScrollLock = null;
  let pendingUnlockTimer = 0;

  const getDrawer = () => document.getElementById('cart-drawer');

  function lockPageScroll() {
    if (!mobileQuery.matches || pageScrollLock || !document.body) return;

    window.clearTimeout(pendingUnlockTimer);
    pendingUnlockTimer = 0;

    const root = document.documentElement;
    const body = document.body;
    const scrollX = window.scrollX || window.pageXOffset || 0;
    const scrollY = window.scrollY || window.pageYOffset || 0;

    pageScrollLock = {
      scrollX,
      scrollY,
      rootOverflow: root.style.overflow,
      rootOverscrollBehavior: root.style.overscrollBehavior,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyOverflow: body.style.overflow,
      bodyOverscrollBehavior: body.style.overscrollBehavior
    };

    root.classList.add('qls-mobile-cart-scroll-locked');
    root.style.overflow = 'hidden';
    root.style.overscrollBehavior = 'none';

    // iOS can continue moving the page behind a fixed overlay when only
    // overflow:hidden is used. Freezing the body at its current page offset
    // prevents that background movement while preserving nested cart scroll.
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = `-${scrollX}px`;
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
  }

  function unlockPageScroll() {
    if (!pageScrollLock || !document.body) return;

    const root = document.documentElement;
    const body = document.body;
    const saved = pageScrollLock;
    pageScrollLock = null;

    root.classList.remove('qls-mobile-cart-scroll-locked');
    root.style.overflow = saved.rootOverflow;
    root.style.overscrollBehavior = saved.rootOverscrollBehavior;
    body.style.position = saved.bodyPosition;
    body.style.top = saved.bodyTop;
    body.style.left = saved.bodyLeft;
    body.style.right = saved.bodyRight;
    body.style.width = saved.bodyWidth;
    body.style.overflow = saved.bodyOverflow;
    body.style.overscrollBehavior = saved.bodyOverscrollBehavior;

    // Restore the exact page position after removing position:fixed.
    window.requestAnimationFrame(() => {
      window.scrollTo(saved.scrollX, saved.scrollY);
    });
  }

  function schedulePageScrollUnlock(delay = 320) {
    window.clearTimeout(pendingUnlockTimer);
    pendingUnlockTimer = window.setTimeout(() => {
      pendingUnlockTimer = 0;
      if (!drawerIsOpen(getDrawer())) unlockPageScroll();
    }, delay);
  }

  function synchronizePageScrollLock() {
    const drawerOpen = drawerIsOpen(getDrawer());

    if (mobileQuery.matches && drawerOpen) {
      lockPageScroll();
    } else if (!mobileQuery.matches) {
      unlockPageScroll();
    } else if (pageScrollLock) {
      schedulePageScrollUnlock();
    }
  }

  function getParts() {
    const drawer = getDrawer();
    if (!drawer) return {};

    return {
      drawer,
      scroller: drawer.querySelector('.drawer__content'),
      summary: drawer.querySelector('.cart-drawer__summary'),
      toggle: drawer.querySelector('[data-qls-mobile-cart-summary-toggle]'),
      panel: drawer.querySelector('[data-qls-mobile-cart-summary-panel]')
    };
  }

  function drawerIsOpen(drawer) {
    return Boolean(
      drawer
      && (
        drawer.hasAttribute('open')
        || drawer.getAttribute('aria-hidden') === 'false'
      )
    );
  }

  function modalLayerIsOpen() {
    return document.documentElement.classList.contains('qls-cart-action-dialog-open')
      || document.documentElement.classList.contains('qls-protection-dialog-open');
  }

  function getOpenUtility(drawer = getDrawer()) {
    if (!drawer) return null;

    return Array.from(drawer.querySelectorAll('cart-drawer-disclosure'))
      .find((disclosure) => disclosure.querySelector('details[open]')) || null;
  }

  function setExpanded(expanded, focusToggle = false) {
    const { summary, toggle, panel } = getParts();
    if (!summary || !toggle || !panel) return;

    const shouldExpand = mobileQuery.matches && expanded;
    summary.classList.toggle('qls-mobile-summary-open', shouldExpand);
    toggle.setAttribute('aria-expanded', shouldExpand ? 'true' : 'false');
    panel.setAttribute('aria-hidden', shouldExpand ? 'false' : 'true');

    if (focusToggle) toggle.focus({ preventScroll: true });
  }

  function closeUtility(disclosure) {
    if (!disclosure?.isConnected) return;

    const details = disclosure.querySelector('details');
    if (!details?.open) return;

    if (typeof disclosure.close === 'function') {
      disclosure.close();
    } else {
      details.open = false;
    }
  }

  function closeUtilityAndSummary(disclosure = getOpenUtility()) {
    closeUtility(disclosure);
    setExpanded(false);
  }

  function initializeCurrentPanel() {
    const { panel } = getParts();
    if (!panel || panel === initializedPanel) return;

    initializedPanel = panel;
    setExpanded(false);
  }

  function getTouchPoint(event) {
    return event.touches?.[0] || event.changedTouches?.[0] || null;
  }

  function beginOutsideGesture(event) {
    if (!mobileQuery.matches || modalLayerIsOpen()) return;

    const { drawer, summary } = getParts();
    if (!drawerIsOpen(drawer)) return;

    const point = getTouchPoint(event);
    if (!point) return;

    const utility = getOpenUtility(drawer);
    if (utility) {
      const utilityContent = utility.querySelector('.cart-drawer-popup__content');
      if (utilityContent && !utilityContent.contains(event.target)) {
        outsideGesture = {
          type: 'utility',
          disclosure: utility,
          startX: point.clientX,
          startY: point.clientY
        };
      }
      return;
    }

    if (
      summary?.classList.contains('qls-mobile-summary-open')
      && !summary.contains(event.target)
    ) {
      outsideGesture = {
        type: 'summary',
        startX: point.clientX,
        startY: point.clientY
      };
    }
  }

  function handleOutsideGestureMove(event) {
    if (!outsideGesture || modalLayerIsOpen()) return;

    const point = getTouchPoint(event);
    if (!point) return;

    const distanceX = Math.abs(point.clientX - outsideGesture.startX);
    const distanceY = Math.abs(point.clientY - outsideGesture.startY);
    if (Math.max(distanceX, distanceY) < gestureThreshold) return;

    if (outsideGesture.type === 'utility') {
      // Consume the first backdrop movement so the cart cannot rubber-band
      // underneath the utility sheet while it is being dismissed.
      event.preventDefault();
      closeUtilityAndSummary(outsideGesture.disclosure);
    } else {
      // Do not prevent the gesture here: the cart item area should continue
      // scrolling naturally as the order-details sheet collapses.
      setExpanded(false);
    }

    outsideGesture = null;
  }

  function stopPanelScroll(event) {
    if (!mobileQuery.matches || modalLayerIsOpen()) return;

    const { drawer, summary } = getParts();
    if (!drawerIsOpen(drawer) || !summary?.classList.contains('qls-mobile-summary-open')) return;

    // Utility sheets have their own intentionally scrollable content area.
    if (event.target.closest('.cart-drawer-popup__content')) return;

    if (summary.contains(event.target)) {
      event.preventDefault();
    }
  }

  function ensureActionDialog() {
    if (actionDialog?.isConnected) return actionDialog;

    actionDialog = document.createElement('div');
    actionDialog.className = 'qls-cart-action-dialog';
    actionDialog.hidden = true;
    actionDialog.innerHTML = `
      <div class="qls-cart-action-dialog__backdrop" data-qls-cart-dialog-close></div>
      <section class="qls-cart-action-dialog__panel" role="dialog" aria-modal="true" aria-labelledby="qls-cart-action-dialog-title" tabindex="-1">
        <button type="button" class="qls-cart-action-dialog__close" data-qls-cart-dialog-close aria-label="Close">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 5l14 14M19 5 5 19" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="1.8"/></svg>
        </button>

        <div data-qls-cart-dialog-view="empty" hidden>
          <p class="qls-cart-action-dialog__eyebrow">Cart tools</p>
          <h2 id="qls-cart-action-dialog-title">Empty your cart?</h2>
          <p class="qls-cart-action-dialog__copy">This removes every item so you can start again.</p>
          <p class="qls-cart-action-dialog__status" data-qls-empty-cart-status role="status" aria-live="polite"></p>
          <div class="qls-cart-action-dialog__actions">
            <button type="button" class="qls-cart-action-dialog__button qls-cart-action-dialog__button--secondary" data-qls-cart-dialog-close>Cancel</button>
            <button type="button" class="qls-cart-action-dialog__button qls-cart-action-dialog__button--danger" data-qls-confirm-empty-cart>Empty cart</button>
          </div>
        </div>

        <div data-qls-cart-dialog-view="share" hidden>
          <p class="qls-cart-action-dialog__eyebrow">Cart tools</p>
          <h2 id="qls-cart-share-dialog-title">Share this cart</h2>
          <p class="qls-cart-action-dialog__copy">Anyone with this link can open a fresh cart with the same products and quantities.</p>
          <label class="qls-cart-action-dialog__label" for="qls-share-cart-link">Cart link</label>
          <div class="qls-cart-action-dialog__copy-row">
            <input id="qls-share-cart-link" class="qls-cart-action-dialog__input" type="text" readonly data-qls-share-cart-link value="Creating link…">
            <button type="button" class="qls-cart-action-dialog__copy-button" data-qls-copy-cart-link disabled>Copy link</button>
          </div>
          <p class="qls-cart-action-dialog__status" data-qls-share-cart-status role="status" aria-live="polite"></p>
          <button type="button" class="qls-cart-action-dialog__button qls-cart-action-dialog__button--primary qls-cart-action-dialog__native-share" data-qls-native-share-cart hidden>Share…</button>
        </div>
      </section>`;

    document.body.appendChild(actionDialog);
    return actionDialog;
  }

  function setDialogTitleReference(view) {
    const panel = actionDialog?.querySelector('.qls-cart-action-dialog__panel');
    if (!panel) return;
    panel.setAttribute(
      'aria-labelledby',
      view === 'share' ? 'qls-cart-share-dialog-title' : 'qls-cart-action-dialog-title'
    );
  }

  function setDialogView(view) {
    const dialog = ensureActionDialog();
    dialog.querySelectorAll('[data-qls-cart-dialog-view]').forEach((node) => {
      node.hidden = node.dataset.qlsCartDialogView !== view;
    });
    setDialogTitleReference(view);
  }

  function openActionDialog(view, opener) {
    const dialog = ensureActionDialog();
    actionDialogOpener = opener || null;
    setDialogView(view);
    dialog.hidden = false;
    document.documentElement.classList.add('qls-cart-action-dialog-open');
    window.setTimeout(() => {
      dialog.querySelector('.qls-cart-action-dialog__panel')?.focus({ preventScroll: true });
    }, 20);
  }

  function closeActionDialog() {
    if (!actionDialog) return;
    actionDialog.hidden = true;
    document.documentElement.classList.remove('qls-cart-action-dialog-open');
    shareCartUrl = '';

    const opener = actionDialogOpener;
    actionDialogOpener = null;
    if (opener?.isConnected) {
      window.setTimeout(() => opener.focus({ preventScroll: true }), 20);
    }
  }

  async function fetchCartJson() {
    const cartRoute = window.theme?.routes?.cart || '/cart';
    const response = await fetch(`${cartRoute}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    const cart = await response.json();
    if (!response.ok) throw new Error(cart?.description || cart?.message || 'Unable to load the cart.');
    return cart;
  }

  function normalize(value) {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  function getProtectionConfig() {
    const config = document.querySelector('[data-qls-shipping-protection-config]');
    if (!config) return {};

    return {
      handle: normalize(config.dataset.productHandle),
      title: normalize(config.dataset.productTitle),
      vendor: normalize(config.dataset.productVendor)
    };
  }

  function isProtectionItem(item, config) {
    const handle = normalize(item.handle);
    const title = normalize(item.product_title || item.title);
    const vendor = normalize(item.vendor);

    if (config.handle && handle === config.handle) return true;
    if (config.title && title === config.title) return true;
    return Boolean(
      config.vendor
      && vendor === config.vendor
      && /(protect|insurance)/.test(title)
    );
  }

  function publicProperties(item) {
    const properties = item?.properties;
    if (!properties || typeof properties !== 'object') return {};

    return Object.fromEntries(
      Object.entries(properties).filter(([key, value]) => (
        key
        && !key.startsWith('_')
        && value !== null
        && value !== undefined
        && String(value).trim() !== ''
      ))
    );
  }

  function encodeBase64Url(value) {
    const bytes = new TextEncoder().encode(value);
    let binary = '';
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  function buildShareCartUrl(cart) {
    const protectionConfig = getProtectionConfig();
    const merchandise = (cart.items || []).filter((item) => (
      Number(item.quantity) > 0 && !isProtectionItem(item, protectionConfig)
    ));

    if (!merchandise.length) throw new Error('There are no products available to share.');

    const totals = new Map();
    const orderedVariantIds = [];

    merchandise.forEach((item) => {
      const variantId = String(item.variant_id || item.id || '');
      if (!/^\d+$/.test(variantId)) return;
      if (!totals.has(variantId)) orderedVariantIds.push(variantId);
      totals.set(variantId, (totals.get(variantId) || 0) + Number(item.quantity || 0));
    });

    const pairs = orderedVariantIds
      .filter((variantId) => totals.get(variantId) > 0)
      .map((variantId) => `${variantId}:${totals.get(variantId)}`);

    if (!pairs.length) throw new Error('There are no products available to share.');

    const cartRoute = String(window.theme?.routes?.cart || '/cart').replace(/\/$/, '');
    const params = new URLSearchParams({ storefront: 'true' });
    const warnings = [];

    const propertyItems = merchandise.filter((item) => Object.keys(publicProperties(item)).length > 0);
    if (propertyItems.length === 1 && propertyItems[0] === merchandise[0]) {
      params.set('properties', encodeBase64Url(JSON.stringify(publicProperties(propertyItems[0]))));
    } else if (propertyItems.length > 0) {
      warnings.push('Custom item options might need to be selected again.');
    }

    if (merchandise.some((item) => item.selling_plan_allocation)) {
      warnings.push('Subscription timing is not included in the shared link.');
    }

    const origin = window.location.origin && window.location.origin !== 'null'
      ? window.location.origin
      : document.baseURI;

    return {
      url: new URL(`${cartRoute}/${pairs.join(',')}?${params.toString()}`, origin).href,
      warning: warnings.join(' ')
    };
  }

  async function prepareShareCart() {
    const dialog = ensureActionDialog();
    const input = dialog.querySelector('[data-qls-share-cart-link]');
    const copyButton = dialog.querySelector('[data-qls-copy-cart-link]');
    const shareButton = dialog.querySelector('[data-qls-native-share-cart]');
    const status = dialog.querySelector('[data-qls-share-cart-status]');

    input.value = 'Creating link…';
    copyButton.disabled = true;
    shareButton.hidden = true;
    status.textContent = '';

    try {
      const cart = await fetchCartJson();
      const result = buildShareCartUrl(cart);
      shareCartUrl = result.url;
      input.value = shareCartUrl;
      copyButton.disabled = false;
      shareButton.hidden = typeof navigator.share !== 'function';
      status.textContent = result.warning || 'Your cart link is ready.';
    } catch (error) {
      shareCartUrl = '';
      input.value = '';
      status.textContent = error.message || 'The cart link could not be created.';
    }
  }

  async function copyShareCartLink() {
    if (!shareCartUrl) return;

    const dialog = ensureActionDialog();
    const input = dialog.querySelector('[data-qls-share-cart-link]');
    const status = dialog.querySelector('[data-qls-share-cart-status]');

    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(shareCartUrl);
      } else {
        input.focus({ preventScroll: true });
        input.select();
        input.setSelectionRange(0, input.value.length);
        if (!document.execCommand('copy')) throw new Error('Copy failed');
      }
      status.textContent = 'Cart link copied.';
    } catch (error) {
      input.focus({ preventScroll: true });
      input.select();
      input.setSelectionRange(0, input.value.length);
      status.textContent = 'Press and hold the link to copy it.';
    }
  }

  async function nativeShareCart() {
    if (!shareCartUrl || typeof navigator.share !== 'function') return;

    const status = ensureActionDialog().querySelector('[data-qls-share-cart-status]');
    try {
      await navigator.share({
        title: 'Quality Liquor Store cart',
        text: 'Here is my Quality Liquor Store cart.',
        url: shareCartUrl
      });
      status.textContent = 'Cart shared.';
    } catch (error) {
      if (error?.name !== 'AbortError') status.textContent = 'The share menu could not be opened.';
    }
  }

  function getCartSections(drawer) {
    const drawerSection = drawer?.closest('.shopify-section')?.id?.replace('shopify-section-', '');
    return [...new Set([
      drawerSection,
      'cart-icon-bubble',
      'free-shipping-notice'
    ].filter(Boolean))];
  }

  async function emptyCart() {
    const dialog = ensureActionDialog();
    const confirmButton = dialog.querySelector('[data-qls-confirm-empty-cart]');
    const status = dialog.querySelector('[data-qls-empty-cart-status]');
    const drawer = getDrawer();

    confirmButton.disabled = true;
    confirmButton.textContent = 'Emptying…';
    status.textContent = '';

    try {
      const cart = await fetchCartJson();
      if (!cart.items?.length) {
        closeActionDialog();
        return;
      }

      const updates = {};
      cart.items.forEach((item) => {
        if (item.key) updates[item.key] = 0;
      });

      const sections = getCartSections(drawer);
      const response = await fetch(window.theme?.routes?.cartUpdate || '/cart/update', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          updates,
          sections,
          sections_url: window.location.pathname
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.description || data?.message || 'The cart could not be emptied.');

      closeActionDialog();
      setExpanded(false);

      if (drawer && data.sections && typeof drawer.renderContents === 'function') {
        await drawer.renderContents(data, false);
      } else {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      }

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { source: 'empty-cart', cart: data }
      }));
    } catch (error) {
      status.textContent = error.message || 'The cart could not be emptied. Please try again.';
    } finally {
      confirmButton.disabled = false;
      confirmButton.textContent = 'Empty cart';
    }
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const dialogClose = event.target.closest('[data-qls-cart-dialog-close]');
    if (dialogClose && actionDialog?.contains(dialogClose)) {
      event.preventDefault();
      closeActionDialog();
      return;
    }

    const confirmEmpty = event.target.closest('[data-qls-confirm-empty-cart]');
    if (confirmEmpty && actionDialog?.contains(confirmEmpty)) {
      event.preventDefault();
      emptyCart();
      return;
    }

    const copyLink = event.target.closest('[data-qls-copy-cart-link]');
    if (copyLink && actionDialog?.contains(copyLink)) {
      event.preventDefault();
      copyShareCartLink();
      return;
    }

    const nativeShare = event.target.closest('[data-qls-native-share-cart]');
    if (nativeShare && actionDialog?.contains(nativeShare)) {
      event.preventDefault();
      nativeShareCart();
      return;
    }

    const shareCart = event.target.closest('[data-qls-share-cart]');
    if (shareCart) {
      event.preventDefault();
      closeUtilityAndSummary();
      openActionDialog('share', shareCart);
      prepareShareCart();
      return;
    }

    const emptyCartButton = event.target.closest('[data-qls-empty-cart]');
    if (emptyCartButton) {
      event.preventDefault();
      openActionDialog('empty', emptyCartButton);
      const status = ensureActionDialog().querySelector('[data-qls-empty-cart-status]');
      status.textContent = '';
      return;
    }

    const toggle = event.target.closest('[data-qls-mobile-cart-summary-toggle]');
    if (!toggle) return;

    setExpanded(toggle.getAttribute('aria-expanded') !== 'true');
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && actionDialog && !actionDialog.hidden) closeActionDialog();
  });

  document.addEventListener('scroll', (event) => {
    if (!mobileQuery.matches || modalLayerIsOpen()) return;

    const { drawer, scroller, summary } = getParts();
    if (!drawerIsOpen(drawer)) return;

    const utility = getOpenUtility(drawer);
    const utilityContent = utility?.querySelector('.cart-drawer-popup__content');
    if (utility && utilityContent && !utilityContent.contains(event.target)) {
      closeUtilityAndSummary(utility);
      return;
    }

    if (
      summary?.classList.contains('qls-mobile-summary-open')
      && event.target === scroller
    ) {
      setExpanded(false);
    }
  }, true);

  document.addEventListener('wheel', (event) => {
    if (!mobileQuery.matches || modalLayerIsOpen()) return;

    const { drawer, summary } = getParts();
    if (!drawerIsOpen(drawer)) return;

    const utility = getOpenUtility(drawer);
    if (utility) {
      const utilityContent = utility.querySelector('.cart-drawer-popup__content');
      if (utilityContent && !utilityContent.contains(event.target)) {
        event.preventDefault();
        closeUtilityAndSummary(utility);
      }
      return;
    }

    if (!summary?.classList.contains('qls-mobile-summary-open')) return;

    if (summary.contains(event.target)) {
      event.preventDefault();
    } else {
      setExpanded(false);
    }
  }, { capture: true, passive: false });

  document.addEventListener('touchstart', beginOutsideGesture, { capture: true, passive: true });
  document.addEventListener('touchmove', handleOutsideGestureMove, { capture: true, passive: false });
  document.addEventListener('touchmove', stopPanelScroll, { capture: true, passive: false });
  document.addEventListener('touchend', () => { outsideGesture = null; }, { capture: true, passive: true });
  document.addEventListener('touchcancel', () => { outsideGesture = null; }, { capture: true, passive: true });

  document.addEventListener('on:cart-drawer:before-open', () => {
    lockPageScroll();
  });

  document.addEventListener('on:cart-drawer:after-open', () => {
    lockPageScroll();
  });

  document.addEventListener('dispatch:cart-drawer:open', () => {
    lockPageScroll();
    initializeCurrentPanel();
    setExpanded(false);
  });

  document.addEventListener('dispatch:cart-drawer:refresh', () => {
    window.setTimeout(() => {
      initializeCurrentPanel();
    }, 180);
  });

  document.addEventListener('on:cart-drawer:before-close', () => {
    outsideGesture = null;
    closeActionDialog();
    setExpanded(false);
    // Keep the page frozen through the drawer's close transition.
    schedulePageScrollUnlock();
  });

  document.addEventListener('on:cart-drawer:after-close', () => {
    window.clearTimeout(pendingUnlockTimer);
    pendingUnlockTimer = 0;
    unlockPageScroll();
  });

  mobileQuery.addEventListener?.('change', () => {
    outsideGesture = null;
    if (!mobileQuery.matches) {
      setExpanded(false);
      unlockPageScroll();
    } else {
      synchronizePageScrollLock();
    }
  });

  const observer = new MutationObserver(() => {
    window.requestAnimationFrame(() => {
      initializeCurrentPanel();
      synchronizePageScrollLock();
    });
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  initializeCurrentPanel();
  synchronizePageScrollLock();
})();
